/**
 * Xero Sync Preview Component
 *
 * Feature: F008 - Xero Wizard Integration & Sync Preview (Sprint 24)
 *
 * Displays a summary of the Xero sync result before the user confirms and saves
 * the imported financial data. Shows organisation name, period dates, trust grade,
 * P&L and BS item counts with totals, and unmapped account count.
 *
 * Props:
 *   financialPeriodId   — UUID of the financial period
 *   entityRecordId      — UUID of the entity financial record
 *   connectionId        — UUID of the Xero connection
 *   orgName             — Xero organisation name
 *   syncResult          — Result from XeroMappingEditor.onComplete
 *   trustGrade          — "xero_finalised" (green) or "xero_draft" (amber)
 *   periodStartDate     — Period start date string (YYYY-MM-DD)
 *   periodEndDate       — Period end date string (YYYY-MM-DD)
 *   onConfirm           — Called after successful save
 *   onBackToMapping     — Called when user clicks Back to Mapping
 *   onCancel            — Called when user cancels
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import type { StandardisedResult } from './xero-mapping-editor';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface XeroSyncPreviewProps {
  financialPeriodId: string;
  entityRecordId: string;
  connectionId: string;
  orgName: string;
  syncResult: StandardisedResult;
  trustGrade: 'xero_finalised' | 'xero_draft';
  periodStartDate: string;
  periodEndDate: string;
  onConfirm: () => void;
  onBackToMapping: () => void;
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function XeroSyncPreview({
  financialPeriodId,
  entityRecordId,
  connectionId: _connectionId,
  orgName,
  syncResult,
  trustGrade,
  periodStartDate,
  periodEndDate,
  onConfirm,
  onBackToMapping,
  onCancel,
}: XeroSyncPreviewProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // -------------------------------------------------------------------------
  // Save mutation — triggers standardise if data not already saved
  // -------------------------------------------------------------------------

  const confirmMutation = useMutation({
    mutationFn: async () => {
      // Data was already standardised by XeroMappingEditor — we just need to
      // invalidate the cache and close. If standardise is needed again, call it.
      // For now: POST to standardise endpoint to ensure data is persisted.
      const { apiRequest } = await import('@/lib/queryClient');
      const res = await apiRequest('POST', '/api/xero/standardise', {
        financialPeriodId,
        entityRecordId,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to save financial data');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/business-analytics'] });
      queryClient.invalidateQueries({ queryKey: ['/api/financial'] });
      toast({
        title: 'Financial data imported from Xero',
        description: `Imported from ${orgName}`,
      });
      onConfirm();
    },
    onError: (error: Error) => {
      toast({
        title: 'Save failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // -------------------------------------------------------------------------
  // Trust grade display
  // -------------------------------------------------------------------------

  const isFinalised = trustGrade === 'xero_finalised';
  const trustBadgeVariant = isFinalised ? 'default' : 'secondary';
  const trustBadgeLabel = isFinalised ? 'Xero Finalised' : 'Xero Draft';
  const trustGradeLabel = isFinalised ? 'Credit Grade' : 'Insight Grade';

  // -------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-4" data-testid="xero-sync-preview">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold">Confirm Financial Data</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Review the summary below before saving
        </p>
      </div>

      {/* Summary Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            Sync Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Organisation */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Xero Organisation</span>
            <span className="text-sm font-medium" data-testid="preview-org-name">
              {orgName}
            </span>
          </div>

          {/* Period dates */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Period</span>
            <span className="text-sm font-medium" data-testid="preview-period-dates">
              {formatDate(periodStartDate)} — {formatDate(periodEndDate)}
            </span>
          </div>

          {/* Data Classification badge */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Data Classification</span>
            <Badge
              variant={trustBadgeVariant}
              className={isFinalised ? 'bg-green-600' : 'bg-amber-500'}
              data-testid="badge-trust-grade"
            >
              {trustBadgeLabel}
            </Badge>
          </div>

          {/* Trust grade */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Credit Grade</span>
            <span className="text-sm font-medium" data-testid="preview-trust-grade-label">
              {trustGradeLabel}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Items Summary Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Imported Items</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* P&L Items */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">P&amp;L Line Items</span>
            <span
              className="text-sm font-medium"
              data-testid="preview-pl-count"
            >
              {syncResult.profitLossItems} items
            </span>
          </div>

          {/* BS Items */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Balance Sheet Items</span>
            <span
              className="text-sm font-medium"
              data-testid="preview-bs-count"
            >
              {syncResult.balanceSheetItems} items
            </span>
          </div>

          {/* Unmapped */}
          {syncResult.unmappedAccounts > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                Unmapped Accounts
              </span>
              <span
                className="text-sm font-medium text-amber-600"
                data-testid="preview-unmapped-count"
              >
                {syncResult.unmappedAccounts} excluded
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-between items-center pt-2">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onBackToMapping}
            disabled={confirmMutation.isPending}
            data-testid="button-back-to-mapping"
          >
            Back to Mapping
          </Button>
          <Button
            variant="ghost"
            onClick={() => setShowCancelDialog(true)}
            disabled={confirmMutation.isPending}
            data-testid="button-cancel-preview"
          >
            Cancel
          </Button>
        </div>

        <Button
          onClick={() => confirmMutation.mutate()}
          disabled={confirmMutation.isPending}
          data-testid="button-confirm-save"
          className="min-w-[200px]"
        >
          {confirmMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Saving Xero financial data...
            </>
          ) : (
            'Confirm & Save Financial Data'
          )}
        </Button>
      </div>

      {/* Cancel confirmation dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent data-testid="dialog-cancel-wizard">
          <AlertDialogHeader>
            <AlertDialogTitle>Exit wizard?</AlertDialogTitle>
            <AlertDialogDescription>
              Your mapping progress will be lost. No financial data will be saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              data-testid="button-stay-in-wizard"
            >
              Stay
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowCancelDialog(false);
                onCancel();
              }}
              data-testid="button-confirm-cancel-wizard"
            >
              Exit Wizard
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
